import { useState, useMemo, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../../Components/UI/dialog"
import { Drawer, DrawerContent, DrawerClose, DrawerFooter } from "../../../Components/UI/drawer"
import {
    X,
    Plus,
    User,
    MapPin,
    History,
    Filter,
    FileText,
    ArrowLeft,
    Search,
    Edit2,
} from "lucide-react"
import { Button } from "../../../Components/UI/button"
import { Input } from "../../../Components/UI/input"
import { Badge } from "../../../Components/UI/badge"
import { cn } from "../../../Lib/Cn"
import { getDateWithoutTime, getRelativeDateText as utilGetRelativeDateText } from "../../../Utils/Utilities"
import { CommonFilter } from "../../../Components/Common/CommonFilter";
import { DrawerFilter } from "../../../Components/Common/DrawerFilter"
import { useSelector } from "react-redux";
import { checkIfInqOnlyMode, getUsersArr } from "../../../Store/Reducers"
import { useNavigate } from 'react-router-dom';
import ThreadSahiIcon from "../../../Components/Common/ThreadSahiIcon"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@radix-ui/react-hover-card"
import useIsMobile from "../../../Hooks/useIsMobile"
import { formatRecordText } from "../../../Utils/Utilities";
import { RiResetLeftFill } from "react-icons/ri"
import { getAuthData } from "../../../Utils/AuthUtils";
import RecordEntry from "../../Records/RecordsEntry"

// Filter button styling constant
const filterBtn = "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px]";

const RecordHistoryDialog = ({
    isOpen,
    onClose,
    threadData = [],
    threadTypes = [],
    records,
    getExecutiveName,
    getRelativeDateText = utilGetRelativeDateText,
    onRecordEntryOpen,
    connectionName,
    users = [],
    connID,
    onRefreshRecords,
    recordTypeFilter = null,
    connectionData = [],
    connIDs = [],
    isDashboardCall = false,
    pendingRIDs = null
}) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [selectedFilters, setSelectedFilters] = useState({
        recordType: "all",
        executive: "all",
        dateRange: "all",
        finYear: "all",
        otherMonth: "all",
        connection: "all"
    });

    const [tempFilters, setTempFilters] = useState(selectedFilters);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isFilterSelected, setIsFilterSelected] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRecordForEdit, setSelectedRecordForEdit] = useState(null);
    const [isRecordEditOpen, setIsRecordEditOpen] = useState(false);
    const [localRecords, setLocalRecords] = useState(records);

    // Initialize filter based on recordTypeFilter prop
    useEffect(() => {
        setLocalRecords(records);
    }, [records]);

    useEffect(() => {
        if (recordTypeFilter && isOpen) {
            setSelectedFilters(prev => ({
                ...prev,
                recordType: recordTypeFilter
            }));
            setTempFilters(prev => ({
                ...prev,
                recordType: recordTypeFilter
            }));
            setIsFilterSelected(true);
        }
    }, [recordTypeFilter, isOpen]);

    const getDateRangeOptions = () => [
        "This Month",
        "Last Month",
        // "Other Month",
        // "This Quarter",
        // "Prev Quarter",
        //  "Fin Year",

    ];

    const isRecordEditable = (record) => {
        const recordDate = new Date(record.DT);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const isToday = recordDate.toDateString() === today.toDateString();
        const isYesterday = recordDate.toDateString() === yesterday.toDateString();
        const isWithinEditableWindow = isToday || isYesterday;

        if (Array.isArray(pendingRIDs)) {
            return isWithinEditableWindow && record?.RID && pendingRIDs.includes(String(record.RID));
        }

        // Get current user ID
        const authData = getAuthData();
        const currentUserID = authData?.UserID;

        const isCallOrMessage = record.Typ && (record.Typ.toLowerCase() === 'call' || record.Typ.toLowerCase() === 'message') && (record.Txt.toLowerCase() === 'dialed call.' || record.Txt.toLowerCase() === 'sent message.');
        const isAssignedToCurrentUser = Number(record.UID) === Number(currentUserID);

        return isWithinEditableWindow && isCallOrMessage && isAssignedToCurrentUser;
    };

    // Get record types from Redux store (same as RecordEntry)
    const recordTypesData = useSelector((state) => state.masterData.recordTypes);
    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
    const usersFromRedux = useSelector(getUsersArr);

    const resolveGetRelativeDateText = (date, isOnlyTime = false, showTime = true, showYear = true, shortMonth = false, showYearAlways = false) => {
        if (typeof getRelativeDateText === 'function') {
            return getRelativeDateText(date, isOnlyTime, showTime, showYear, shortMonth, showYearAlways);
        }
        return utilGetRelativeDateText(date, isOnlyTime, showTime, showYear, shortMonth, showYearAlways);
    };

    const resolveExecutiveName = (uid) => {
        if (typeof getExecutiveName === 'function') {
            return getExecutiveName(uid);
        }
        const user = (usersFromRedux || []).find(u => (u.userID || u.id) == uid);
        return user?.name || user?.userName || uid || '';
    };

    // Check if any filter is applied (not default value)
    const hasActiveFilters = Object.entries(selectedFilters).some(([key, value]) => {
        return value !== "all";
    });

    // Dynamic record type options from Redux
    const getRecordTypeOptions = () => {
        // If recordTypesData contains objects with Typ property
        if (recordTypesData && recordTypesData.length > 0) {
            // Check if it's an array of objects with Typ property
            if (typeof recordTypesData[0] === 'object' && recordTypesData[0].Typ) {
                return recordTypesData.map(recordType => recordType.Typ);
            }
            // If it's already an array of strings
            return recordTypesData;
        }
        return [];
    };


    // Dynamic executive options from users prop
    const getExecutiveOptions = () => {
        if (!usersFromRedux || usersFromRedux.length === 0) return [];

        return usersFromRedux.map((u) => ({
            name: u.name,
            ID: u.userID
        }));
    };

    // Check if we have data for filters
    const hasRecordTypes = recordTypesData && recordTypesData.length > 0;
    const hasUsers = usersFromRedux && usersFromRedux.length > 0;
    const getFinYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const options = [];
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push(`${year}-${year + 1}`);
        }
        return options;
    };

    const getOtherMonthOptions = () => {
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        const options = [];

        for (let i = 0; i <= currentMonth; i++) {
            options.push(`${months[i]} ${currentYear}`);
        }

        return options;
    };

    // 3. Date filtering logic - add this function
    const isRecordInDateRange = (recordDate, dateRange, finYear, otherMonth) => {
        const recordDT = new Date(recordDate);
        const now = new Date();

        // Handle "all" value which represents "This Month" (the default)
        if (dateRange === "all") return true;


        switch (dateRange) {
            case "This Month":
                return recordDT.getMonth() === now.getMonth() &&
                    recordDT.getFullYear() === now.getFullYear();

            case "Last Month":
                const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
                return recordDT.getMonth() === lastMonth.getMonth() &&
                    recordDT.getFullYear() === lastMonth.getFullYear();

            case "This Quarter":
                const currentQuarter = Math.floor(now.getMonth() / 3);
                const recordQuarter = Math.floor(recordDT.getMonth() / 3);
                return recordQuarter === currentQuarter &&
                    recordDT.getFullYear() === now.getFullYear();

            case "Prev Quarter":
                const prevQuarterDate = new Date(now.getFullYear(), now.getMonth() - 3);
                const prevQuarter = Math.floor(prevQuarterDate.getMonth() / 3);
                return Math.floor(recordDT.getMonth() / 3) === prevQuarter &&
                    recordDT.getFullYear() === prevQuarterDate.getFullYear();

            case "Fin Year":
                if (finYear === "all") return true;
                const [startYear, endYear] = finYear.split('-').map(Number);
                const recordYear = recordDT.getFullYear();
                const recordMonth = recordDT.getMonth();

                if (recordMonth >= 3) {
                    return recordYear === startYear;
                } else {
                    return recordYear === endYear;
                }

            case "Other Month":
                if (otherMonth === "all") return true;
                const [monthName, year] = otherMonth.split(' ');
                const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December']
                    .indexOf(monthName);
                return recordDT.getMonth() === monthIndex &&
                    recordDT.getFullYear() === parseInt(year);

            default:
                return true;
        }
    };


    const filterRecords = (records) => {
        return records.filter(record => {
            if (!record) {
                return false;
            }

            if (searchTerm && !record.Txt?.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }

            // Existing filters
            if (selectedFilters.recordType !== "all" && record.Typ !== selectedFilters.recordType) {
                return false;
            }

            if (selectedFilters.executive !== "all") {
                if (selectedFilters.executive === "unassigned" && record.UID) {
                    return false;
                }
                if (selectedFilters.executive !== "unassigned" && record.UID !== selectedFilters.executive) {
                    return false;
                }
            }

            // Add connection filtering for dashboard call
            if (isDashboardCall && selectedFilters.connection !== "all" && record.ConnID !== selectedFilters.connection) {
                return false;
            }

            // Add date filtering
            if (!isRecordInDateRange(record.DT, selectedFilters.dateRange, selectedFilters.finYear, selectedFilters.otherMonth)) {
                return false;
            }

            return true;
        });
    };

    const handleResetFilters = () => {
        const defaultFilters = {
            recordType: "all",
            executive: "all",
            dateRange: "all",
            finYear: "all",
            otherMonth: "all",
            connection: "all"
        };
        setTempFilters(defaultFilters);
        setSelectedFilters(defaultFilters);
        setIsDrawerOpen(false);
        setSearchTerm("");
    };

    const handleApplyFilters = () => {
        setSelectedFilters(tempFilters);
        setIsDrawerOpen(false);
        setIsFilterSelected(false);
    };

    const handleCancelFilters = () => {
        setTempFilters(selectedFilters);
        setIsDrawerOpen(false);
        setIsFilterSelected(false);
    };

    const handleModalClose = () => {
        const defaultFilters = {
            recordType: "all",
            executive: "all",
            dateRange: "all",
            finYear: "all",
            otherMonth: "all",
            connection: "all"
        };
        setSelectedFilters(defaultFilters);
        setTempFilters(defaultFilters);
        setSearchTerm("");
        setIsDrawerOpen(false);
        setIsFilterSelected(false);
        onClose();
    };

    const getBizName = (connID) => {
        if (!isDashboardCall || !connectionData) return null;
        const conn = connectionData.find(c => c.ConnID === connID);
        return conn?.BizName || conn?.ContactName || null;
    };

    // Get connection options for dashboard call
    const getConnectionOptions = () => {
        if (!isDashboardCall || !localRecords) return [];

        const uniqueConnIDs = [...new Set(localRecords.map(r => r.ConnID))];

        const options = uniqueConnIDs
            .map(connID => {
                const conn = connectionData.find(c => c.ConnID === connID);
                if (!conn) return null;

                // If a record type is selected, filter by that type
                if (selectedFilters.recordType !== "all") {
                    const connRecords = localRecords.filter(r => r.ConnID === connID);
                    const hasRecordType = connRecords.some(r => r.Typ === selectedFilters.recordType);
                    if (!hasRecordType) return null;
                }

                return {
                    name: conn.BizName || conn.ContactName || "Unknown",
                    ID: conn.ConnID
                };
            })
            .filter(Boolean);

        return options;
    };

    const formatThreadDisplay = (threadIDs) => {
        if (!threadIDs || !threadIDs.trim()) return null;

        const threadIdArray = threadIDs.split(',').map(id => id.trim());

        return threadIdArray.map((threadId) => {
            const thread = threadData.find(t => t.ThreadID?.toString() === threadId);
            if (!thread) return null;

            const threadType = threadTypes.find(type => type.TID === thread.TypeID);
            const typeName = threadType?.TypSng || 'Thread';
            const productName = thread.MainProduct || 'No product specified';

            // Check if thread has targetDT or sinceDT for hover card
            const hasDateInfo = thread.TargetDT || thread.SinceDT;

            if (hasDateInfo) {
                return (
                    <HoverCard key={threadId}>
                        <HoverCardTrigger asChild>
                            <div className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs font-semibold text-gray-900 mr-2 cursor-pointer">
                                <ThreadSahiIcon tID={thread.TypeID} className="w-4 h-4" />
                                <span>
                                    {typeName} - {productName}
                                </span>
                            </div>
                        </HoverCardTrigger>

                        <HoverCardContent className="bg-white rounded-md shadow-2xl p-3 w-44 text-left" side="top">
                            <h4 className="text-sm font-semibold mb-2 text-gray-900">{typeName}</h4>
                            <div className="space-y-1">
                                {thread.TargetDT && (
                                    <div className="text-sm">
                                        <span className="text-gray-500 font-medium">Due on: </span>
                                        <span className="text-gray-900 font-normal">{getDateWithoutTime(thread.TargetDT)}</span>
                                    </div>
                                )}
                                {thread.SinceDT && (
                                    <div className="text-sm">
                                        <span className="text-gray-500 font-medium">Since: </span>
                                        <span className="text-gray-900 font-normal">{getDateWithoutTime(thread.SinceDT)}</span>
                                    </div>
                                )}
                            </div>
                        </HoverCardContent>
                    </HoverCard>
                );
            } else {
                return (
                    <div
                        key={threadId}
                        className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs font-semibold text-gray-900 mr-2"
                    >
                        <ThreadSahiIcon tID={thread.TypeID} className="w-4 h-4" />
                        <span>
                            {typeName} - {productName}
                        </span>
                    </div>
                );
            }
        }).filter(Boolean);
    };
    const filteredRecords = filterRecords(localRecords);

    const handleRecordSaved = (isDeleted = false, deletedRecordID = null) => {
        if (isDeleted && deletedRecordID) {
            setLocalRecords(prev => prev.filter(record => record.RID !== deletedRecordID));
        } else if (onRefreshRecords) {
            onRefreshRecords();
        }
        setIsRecordEditOpen(false);
        setSelectedRecordForEdit(null);
    };

    // Filter options configuration (matching ThreadsReport structure)
    const filterOptions = [
        // Connection filter - only for dashboard call
        ...(isDashboardCall ? [{
            key: "connection",
            label: "All Contacts",
            options: getConnectionOptions(),
            displayValue: (val) => {
                if (val === "all") return "All Contacts";
                const conn = connectionData.find(c => c.ConnID === val);
                return conn?.BizName || conn?.ContactName || "All Contacts";
            },
            type: "list",
            mobileDropdownText: "Select Contact",
            MobileLabel: "Contact",
            showAllOption: true
        }] : []),
        {
            key: "recordType",
            label: "All Record Types",
            options: getRecordTypeOptions().map((typ) => ({ name: typ, ID: typ })),
            type: "options",
            mobileDropdownText: "Select Record Type",
            MobileLabel: "Record Type",
            showAllOptionNum: false
        },
        // Executive filter - hidden when called from dashboard
        ...(isDashboardCall ? [] : [{
            key: "executive",
            label: "All Executives",
            options: [{ name: "Not Assigned", ID: "unassigned" }, ...getExecutiveOptions()],
            displayValue: (val) => {
                if (val === "unassigned") return "Not Assigned";
                return usersFromRedux.find(u => u.userID === val)?.name || "All Executives";
            },
            type: "list",
            mobileDropdownText: "Select Executive",
            MobileLabel: "Executive",
            showAllOption: true
        }]),
        {
            key: "dateRange",
            label: "All Time",
            options: getDateRangeOptions(),
            searchEnabled: false,
            type: "options",
            MobileLabel: "Period",
            showAllOption: true
        },
        {
            key: "finYear",
            label: "Select Year",
            options: getFinYearOptions(),
            disabled: !(selectedFilters.dateRange === "Fin Year" || tempFilters.dateRange === "Fin Year"),
            mobiledisabled: !(tempFilters.dateRange === "Fin Year"),
            type: "list",
            mobileDropdownText: "Select Financial Year",
            mobileNoLabel: true,
            isSorting: false
        },
        {
            key: "otherMonth",
            label: "Select Month",
            options: getOtherMonthOptions(),
            disabled: !(selectedFilters.dateRange === "Other Month" || tempFilters.dateRange === "Other Month"),
            mobiledisabled: !(tempFilters.dateRange === "Other Month"),
            type: "list",
            mobileDropdownText: "Select Month",
            mobileNoLabel: true,
            isSorting: false
        }
    ];

    // Memoized filter button
    const filterButton = useMemo(() => {
        let activeFilterClass = isFilterSelected && !isMobile ? "filter-active" : "";
        let filterIconToggleClass = "h-4 w-4 " + isMobile ? "" : (isFilterSelected ? "text-sahi-primary-color" : "text-white group-hover:text-sahi-primary-color");
        return (
            <Button
                className={cn(filterBtn, activeFilterClass, "relative")}
                onClick={() => {
                    setTempFilters(selectedFilters);
                    setIsDrawerOpen(true);
                    setIsFilterSelected(true);
                }}
                aria-label="Toggle Filter"
            >
                <div className="h-6 w-6 flex items-center justify-center transition-all">
                    <Filter
                        className={cn(filterIconToggleClass)}
                    />
                    {hasActiveFilters && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-sahi-green rounded-full border border-sahi-white" />}
                </div>
            </Button>
        )
    }, [isFilterSelected, isMobile]);

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleModalClose}>
                <DialogContent className="flex flex-col sm:w-auto sm:max-w-[95vw] lg:max-w-[800px] min-[700px]:min-w-[700px] sm:min-h-0 sm:max-h-[95vh] sm:h-auto sm:rounded-lg rounded-none gap-0">
                    <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                        {/* Mobile View */}
                        <div className="sm:hidden flex items-center justify-between w-full h-8 gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <DialogClose onClick={handleModalClose} tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-6 w-6" />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-base sm:text-lg font-medium">
                                    <div className="truncate">
                                        Records - {connectionName || "Contact"}
                                    </div>
                                </DialogTitle>
                            </div>
                            {/* Right Side Mobile
                        {/* <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 p-0 hover:bg-sahi-blue-light"
                                onClick={() => {
                                    const filterParams = new URLSearchParams();
                                    if (connID) filterParams.set('cid', connID);
                                    if (selectedFilters.recordType !== "all") filterParams.set('rt', selectedFilters.recordType);
                                    if (selectedFilters.executive !== "all") filterParams.set('ex', selectedFilters.executive);
                                    if (selectedFilters.dateRange !== "This month") filterParams.set('dr', selectedFilters.dateRange);
                                    if (selectedFilters.finYear !== "all") filterParams.set('fy', selectedFilters.finYear);
                                    if (selectedFilters.otherMonth !== "all") filterParams.set('om', selectedFilters.otherMonth);

                                    const queryString = filterParams.toString();
                                    navigate(`/records${queryString ? `?${queryString}` : ''}`);
                                    onClose();
                                }}
                            >
                                <FileText className="h-4 w-4" />
                            </Button>
                        </div> */}
                        </div>

                        {/* Desktop View Title */}
                        <div className="hidden sm:flex items-center justify-between w-full">
                            <DialogTitle className="hidden sm:block text-lg md:text-xl">
                                Records - {connectionName || "Contact"}
                            </DialogTitle>

                            {/* Desktop View Actions */}
                            <div className="hidden sm:flex items-center gap-2">
                                {/* <Button
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 p-0 hover:bg-sahi-blue-light"
                                onClick={() => {
                                    const filterParams = new URLSearchParams();
                                    if (connID) filterParams.set('cid', connID);
                                    if (selectedFilters.recordType !== "all") filterParams.set('rt', selectedFilters.recordType);
                                    if (selectedFilters.executive !== "all") filterParams.set('ex', selectedFilters.executive);
                                    if (selectedFilters.dateRange !== "This month") filterParams.set('dr', selectedFilters.dateRange);
                                    if (selectedFilters.finYear !== "all") filterParams.set('fy', selectedFilters.finYear);
                                    if (selectedFilters.otherMonth !== "all") filterParams.set('om', selectedFilters.otherMonth);

                                    const queryString = filterParams.toString();
                                    navigate(`/records${queryString ? `?${queryString}` : ''}`);
                                    onClose();
                                }}
                            >
                                <FileText className="h-4 w-4" />
                            </Button> */}
                                <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={handleModalClose}>
                                    <X className="h-5 w-5" />
                                    <span className="sr-only">Close</span>
                                </DialogClose>
                            </div>
                        </div>
                    </DialogHeader>

                    {/* Mobile Search and Filter Layout */}
                    {isMobile && !isDrawerOpen && (
                        <div className="sm:hidden w-full px-4 py-2 pt-4">
                            <div className="flex items-center gap-2">
                                <div className="relative flex-grow">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                                    <Input
                                        placeholder="Search records"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 h-10 w-full"
                                    />
                                </div>
                                {filterButton}
                            </div>
                        </div>
                    )}

                    {/* Desktop Filters Section */}
                    {(hasRecordTypes || hasUsers || isDashboardCall) && !isMobile && (
                        <div className="hidden sm:block px-4 py-3 border-gray-200 mt-2">
                            <div className="flex flex-wrap gap-2">
                                {filterOptions
                                    .filter(({ key }) => key !== "recordType" && key !== "finYear" && key !== "otherMonth")
                                    .map(({ key, label, options, displayValue, searchEnabled }) => (
                                        <div key={key} className="text-xs">
                                            <CommonFilter
                                                label={label}
                                                options={options}
                                                value={selectedFilters[key]}
                                                onChange={(value) => {
                                                    if (key === "dateRange") {
                                                        setSelectedFilters(prev => ({
                                                            ...prev,
                                                            [key]: value,
                                                            finYear: "all",
                                                            otherMonth: "all"
                                                        }));
                                                    } else {
                                                        setSelectedFilters(prev => ({ ...prev, [key]: value }));
                                                    }
                                                }}
                                                displayValue={displayValue}
                                                searchEnabled={searchEnabled}
                                                mobileDropdownText={`Select ${label}`}
                                            />
                                        </div>
                                    ))}

                                {/* Conditional Fin Year Filter */}
                                {selectedFilters.dateRange === "Fin Year" && (
                                    <div className="text-xs">
                                        <CommonFilter
                                            label="Select Year"
                                            options={getFinYearOptions()}
                                            value={selectedFilters.finYear}
                                            onChange={(value) => setSelectedFilters(prev => ({ ...prev, finYear: value }))}
                                            searchEnabled={false}
                                            mobileDropdownText="Select Financial Year"
                                        />
                                    </div>
                                )}

                                {/* Conditional Other Month Filter */}
                                {selectedFilters.dateRange === "Other Month" && (
                                    <div className="text-xs">
                                        <CommonFilter
                                            label="Select Month"
                                            options={getOtherMonthOptions()}
                                            value={selectedFilters.otherMonth}
                                            onChange={(value) => setSelectedFilters(prev => ({ ...prev, otherMonth: value }))}
                                            searchEnabled={false}
                                            mobileDropdownText="Select Other Month"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Mobile Filter Drawer */}
                    {isMobile && (
                        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                            <DrawerContent className="p-0 [&>div:first-child]:hidden">
                                <div className="f-col h-[100dvh] !rounded-t-[10px]">
                                    {/* Drawer Header */}
                                    <div className="flex items-center justify-between p-4 border-b-2 border-gray-200 bg-bg-white">
                                        <div className="flex gap-4 items-center ">
                                            <DrawerClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                                <ArrowLeft className="h-[24px] w-[24px]" onClick={handleCancelFilters} />
                                                <span className="sr-only">Back</span>
                                            </DrawerClose>
                                            <p className="text-lg font-semibold text-sahi-primary-color">Record Filters</p>
                                        </div>
                                        <button
                                            className="group flex items-center gap-1 text-sm font-light hover:underline transition duration-200 text-sahi-primary-color"
                                            onClick={handleResetFilters}
                                        >
                                            <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                                            <span>Reset</span>
                                        </button>
                                    </div>

                                    {/* Drawer Filter Options */}
                                    <div className="flex-1 overflow-y-auto p-3">
                                        {filterOptions.map(({ key, label, options, displayValue, type, mobiledisabled, mobileDropdownText, MobileLabel, showAllOption, showAllOptionNum, mobileNoLabel, isSorting }) => (
                                            <div key={key} id={key} className="mb-4">
                                                <DrawerFilter
                                                    key={key}
                                                    id={key}
                                                    label={label}
                                                    options={options}
                                                    value={tempFilters[key]}
                                                    displayValue={displayValue}
                                                    onChange={(value) => {
                                                        if (key === "dateRange") {
                                                            setTempFilters((prev) => ({
                                                                ...prev,
                                                                [key]: value,
                                                                finYear: "all",
                                                                otherMonth: "all"
                                                            }));
                                                        } else {
                                                            setTempFilters((prev) => ({
                                                                ...prev,
                                                                [key]: value,
                                                            }));
                                                        }
                                                    }}
                                                    disabled={mobiledisabled}
                                                    type={type}
                                                    mobileDropdownText={mobileDropdownText}
                                                    MobileLabel={MobileLabel}
                                                    showAllOption={showAllOption}
                                                    showAllOptionNum={showAllOptionNum}
                                                    mobileNoLabel={mobileNoLabel}
                                                    isSorting={isSorting}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Drawer Footer */}
                                    <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-sahi-white">
                                        <DrawerClose>
                                            <div className="flex gap-2 w-full">
                                                <Button
                                                    className="sahi-primary-button"
                                                    onClick={handleApplyFilters}
                                                >
                                                    Apply
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    className="sahi-secondary-button"
                                                    onClick={handleCancelFilters}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </DrawerClose>
                                    </DrawerFooter>
                                </div>
                            </DrawerContent>
                        </Drawer>
                    )}

                    <div className="flex-1 overflow-y-auto px-4 pb-4 sm:max-h-[60vh] mt-2">
                        {filteredRecords.length > 0 ? (
                            <div className="space-y-2">
                                {filteredRecords
                                    .sort((a, b) => {
                                        const dateA = new Date(a.DT).getTime();
                                        const dateB = new Date(b.DT).getTime();

                                        if (dateB !== dateA) {
                                            return dateB - dateA; // Newest first
                                        }

                                        // If dates are the same, sort by RID descending (bigger RID on top)
                                        return (Number(b.RID) || 0) - (Number(a.RID) || 0);
                                    }).map((record, index) => {
                                        const hasValidLocation = record.Loc &&
                                            record.Loc.trim() &&
                                            record.Loc.trim() !== ',' &&
                                            record.Loc.trim() !== ', ' &&
                                            record.Loc.replace(/,/g, '').trim().length > 0;

                                        const isEditable = isRecordEditable(record);

                                        return (
                                            <div
                                                key={record.RID || index}
                                                className={`bg-white sahi-radius p-3 border border-sahi-border-color ${isEditable ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
                                                onClick={() => {
                                                    if (isEditable) {
                                                        setSelectedRecordForEdit(record);
                                                        setIsRecordEditOpen(true);
                                                    }
                                                }}
                                            >

                                                {/* Top Row: Type + Date on left, Executive on right */}
                                                <div className="flex items-center justify-between text-xs sm:text-sm text-sub-text-color mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{record.Typ && record.Typ.trim() ? record.Typ : 'Record'}</span>
                                                        <span>@ {resolveGetRelativeDateText(record.DT, false, true, true)}</span>
                                                        {hasValidLocation && (
                                                            <Button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(record.Loc)}`;
                                                                    window.open(mapsUrl, '_blank');
                                                                }}
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-4 w-4 p-0 hover:bg-sahi-blue-light"
                                                                title="View location on map"
                                                            >
                                                                <MapPin className="h-3 w-3 text-sahi-blue" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                    <span className="flex items-center gap-1">
                                                        <User className="h-4 w-4" />
                                                        {resolveExecutiveName(record.UID)}
                                                    </span>
                                                </div>

                                                {/* Notes - bigger & bold */}
                                                <div className="flex items-start gap-2">
                                                    {record.Txt && record.Txt.trim() && (
                                                        <p className={`text-xs sm:text-base text-color flex-1 ${isEditable ? 'mb-2' : 'mb-3'}`}>
                                                            {formatRecordText(record.Txt, true)}
                                                            {isDashboardCall && getBizName(record.ConnID) && (
                                                                <span className="text-sub-text-color ml-2">- {getBizName(record.ConnID)}</span>
                                                            )}
                                                        </p>
                                                    )}
                                                    {isEditable && (
                                                        <Badge variant="default" className="w-fit p-1 sm:p-2 text-sahi-white bg-sahi-green text-[10px] sm:text-xs font-medium gap-1 flex-shrink-0">
                                                            <Edit2 className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                                                            Update Details
                                                        </Badge>
                                                    )}
                                                </div>

                                                {/* Thread Display without label */}
                                                {record.TIDs && record.TIDs.trim() && !isInqOnlyMode && (
                                                    <div className="flex flex-wrap gap-1 text-[10px] sm:text-xs text-sub-text-color">
                                                        {formatThreadDisplay(record.TIDs)}
                                                    </div>
                                                )}
                                            </div>

                                        );
                                    })}

                            </div>
                        ) : (
                            <p class="no-records mb-3 !mt-0 lg:whitespace-nowrap"><span>No records found.</span></p>
                        )}
                    </div>
                    <div className="hidden lg:flex px-4 pb-4 pt-4 justify-start flex-shrink-0">
                        <Button variant="secondary" onClick={() => handleModalClose()}>
                            <X className="h-4 w-4" />
                            Close
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            {/* Record Edit Modal */}
            {selectedRecordForEdit && (
                <RecordEntry
                    triggerButton={false}
                    open={isRecordEditOpen}
                    onOpenChange={(open) => {
                        setIsRecordEditOpen(open);
                        if (!open) {
                            setSelectedRecordForEdit(null);
                        }
                    }}
                    prefilledData={{
                        recordID: selectedRecordForEdit.RID,
                        connID: isDashboardCall ? selectedRecordForEdit.ConnID : connID,
                        recordType: selectedRecordForEdit.Typ,
                        notes: selectedRecordForEdit.Txt,
                        recordDate: selectedRecordForEdit.DT,
                        pocUserID: selectedRecordForEdit.UID,
                        isEditMode: true,
                        connectionLocked: true
                    }}
                    onRecordAdded={(deletionData) => {
                        const isDeleted = deletionData?.isDeleted || false;
                        const deletedID = deletionData?.recordID || selectedRecordForEdit?.RID;
                        handleRecordSaved(isDeleted, deletedID);
                    }}
                />
            )}
        </>
    );
}

export default RecordHistoryDialog;